import { api, type Api } from "@api/bot-base-api-request";
import { useQuery } from "@tanstack/react-query";
import { AutoSellSetting } from "./types";
import { useCallback } from "react";

const BOT_GET_AUTO_SELL_SETTING_QUERY_KEY = "bot-get-auto-sell-setting";

type BotGetAutoSellSettingParams = {
  userId: string;
  appPubKey: string;
};

// Response type from the API
interface BotAutoSellSettingResponse {
  autoSellSetting: AutoSellSetting[];
}

const fetchBotAutoSellSetting = async (
  api: Api,
  params: BotGetAutoSellSettingParams,
): Promise<BotAutoSellSettingResponse> =>
  api.get("getAutoSellSetting", { searchParams: params }).json();

const defaultSelector = (data: BotAutoSellSettingResponse) => data;

const useGetBotAutoSellSetting = <TData = BotAutoSellSettingResponse>(
  params: BotGetAutoSellSettingParams,
  selector: (data: BotAutoSellSettingResponse) => TData = defaultSelector as (
    data: BotAutoSellSettingResponse,
  ) => TData,
) =>
  useQuery<BotAutoSellSettingResponse, Error, TData>({
    queryKey: ["bot-auto-sell-setting", params],
    queryFn: async () => fetchBotAutoSellSetting(api, params),
    placeholderData: (data) => data,
    select: useCallback(selector, [selector]),
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

export {
  useGetBotAutoSellSetting as useGetBotAutoSellSetting,
  fetchBotAutoSellSetting as fetchBotAutoSellSetting,
  BOT_GET_AUTO_SELL_SETTING_QUERY_KEY as BOT_GET_AUTO_SELL_SETTING_QUERY_KEY,
  type BotAutoSellSettingResponse as BotAutoSellSettingResponse,
  type AutoSellSetting as AutoSellSetting,
};
